import { useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useAccount } from '../../contexts/Account';
import {
  submitAOBFStep3,
  updateAdvisorOnboardingStep6,
} from '../../services/advisor_services';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../components/ACSnackbar';
import AdvisoryCloudLogo from '../../components/AdvisoryCloudLogo';
import RatingCard from './RatingCard';

const useStyles = (isSM) => ({
  main: {
    with: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    padding: isSM ? '16px 8px' : '16px 120px 0',
    justifyContent: 'center',
    width: '100%',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '600px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: { xxs: '32px', xs: '24px' },
    letterSpacing: '-0.4300000071525574px',
    marginBottom: '16px',
    padding: { xxs: '0px 8px', xs: '0px' },
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    maxWidth: '360px',
    textAlign: 'center',
  },
  info: {
    fontStyle: 'italic',
    fontSize: '14px',
    fontWeight: 300,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '16px',
  },
  gridContainerProps: {
    container: true,
    spacing: 0,
    justifyContent: 'center',
  },
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
  gridCentered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '8px',
  },
  textField: {
    marginBottom: '8px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        borderColor: '#C7D0DE',
      },
    },
  },
  tagButton: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '12px',
    borderRadius: '16px',
    margin: { xxs: '8px 0px', xs: '8px 0px' },
    padding: '6px 12px',
    width: '100%',
  },
});

function AOBFStep3() {
  const { accountDetails, currentPlan } = useAccount();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = useStyles(isSM);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [salary, setSalary] = useState(0);
  const [interestsSelected, setInterestsSelected] = useState(accountDetails.interests || []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      await submitAOBFStep3({
        id: accountDetails.id,
        interests: interestsSelected,
        annualSalary: salary || 0,
      });
      // Complete AOBF
      await updateAdvisorOnboardingStep6({
        ADVISOR_ID: accountDetails.id,
        ONBOARDING_STATUS: 'Complete',
      });
      window.location.replace(currentPlan.onboardingRedirectUrl);
    } catch (error) {
      setIsLoading(false);
      setOpenSnackbar(true);
      console.error(error);
    }
  };

  const disableButton = () => {
    if (isLoading || interestsSelected.length === 0) {
      return true;
    }
    return false;
  };

  return (
    <>
      <Helmet>
        <title>Profile Builder - AdvisoryCloud</title>
      </Helmet>

      <Box sx={classes.main}>
        <Box sx={classes.header}>
          <AdvisoryCloudLogo
            removeHyperlink
            onClick={() => {}}
            width={isSM ? 240 : 180}
            height={isSM ? 80 : 60}
            cursor="default"
          />
        </Box>

        <Box sx={classes.body}>
          <Typography sx={classes.title}>
            Experience the ROI of AdvisoryCloud
          </Typography>
          <Typography sx={{ ...classes.subtitle, maxWidth: '90%' }}>
            In addition to enjoying the benefits being offered to board members,
            AdvisoryCloud allows you to attract networking and advisory work
            requests that align with your unique interests and expertise.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps} sx={classes.gridCentered}>
                <Typography sx={classes.label}>
                  Which of the following interest you?
                </Typography>
                <Typography sx={classes.info}>Check all that apply:</Typography>
              </Grid>
              <Grid
                {...classes.gridItemProps}
                mb="10px"
                xs={6}
                sm={6}
                md={6}
                sx={classes.gridCentered}
              >
                {[
                  'Board Seats',
                  'Advisory Work',
                  'Networking',
                  'Startup Investments',
                  'Full-Time Roles',
                  'Non-Profit Causes',
                  'Mentoring',
                ].map((tag, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setInterestsSelected((prev) =>
                        prev.includes(tag)
                          ? prev.filter((interest) => interest !== tag)
                          : [...prev, tag]
                      );
                    }}
                    sx={{
                      ...classes.tagButton,
                      backgroundColor: interestsSelected.includes(tag)
                        ? '#2B71F6'
                        : '#F5F7FA',
                      color: interestsSelected.includes(tag)
                        ? '#FFFFFF'
                        : '#232B35',
                      borderColor: interestsSelected.includes(tag)
                        ? '#2B71F6'
                        : '#F5F7FA',
                      '&:hover': {
                        backgroundColor: interestsSelected.includes(tag)
                          ? '#2B71F6'
                          : '#F5F7FA',
                        color: interestsSelected.includes(tag)
                          ? '#FFFFFF'
                          : '#232B35',
                        borderColor: interestsSelected.includes(tag)
                          ? '#2B71F6'
                          : '#F5F7FA',
                      },
                    }}
                  >
                    {tag}
                  </Button>
                ))}
              </Grid>

              <Grid {...classes.gridItemProps}>
                <Typography sx={classes.label}>
                  What is your approximate annual salary? (optional)
                </Typography>
              </Grid>
              <Grid {...classes.gridItemProps}>
                <TextField
                  fullWidth
                  placeholder="$"
                  value={salary ? `$${salary.replace(/\D/g, '')}` : ''}
                  onChange={(e) => {
                    const onlyNumbers = e.target.value.replace(/\D/g, '');
                    setSalary(onlyNumbers);
                  }}
                  inputProps={{ maxLength: 200 }}
                  sx={classes.textField}
                  id="aobf-salary"
                />
              </Grid>
              <Grid {...classes.gridItemProps}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={disableButton()}
                  id="aobf-submit-button"
                  sx={{
                    borderRadius: '50px',
                    backgroundColor: '#3171F6',
                    '&:hover': { backgroundColor: '#3171F6' },
                    height: '48px',
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 700,
                    mb: '16px',
                    mt: '8px',
                  }}
                >
                  {isLoading ? <LoadingLogoSkeleton /> : 'Finish'}
                </Button>
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <RatingCard
                  quote="I have used many other professional networking services/sites with varying degrees of satisfaction - AdvisoryCloud handily beats all of them."
                  name="Sweetser"
                  title="Senior VP Financial, Planning & Analysis"
                />
              </Grid>
            </Grid>
            <ACSnackbar
              onClose={() => setOpenSnackbar(false)}
              open={openSnackbar}
              severity="error"
              text="Something went wrong"
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default AOBFStep3;
